// ShippingDetails.js (FULL code back with TRUCK vs PLANE vs BOAT loader)

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';

import ShippingTable from './ShippingTable';
import ShippingModal from './ShippingModal';
import BulkShippingModal from './BulkShippingModal';
import { deriveBulkShippingSelection, getShippingSupplierKey } from './bulkShippingSelection';

import { fetchShippingList } from '../../services/shipping';
import { getSingleOrderItem } from '../../services/orderItem';
import { fetchOperationsManagementSettings } from '../../services/operationsManagement';

import './index.css';

// ─────────────────────────────
// Truck Loader (inline component)
// ─────────────────────────────
const ShippingTruckLoader = () => {
  return (
    <Box className="shipping-truck-loader">
      <Box className="shipping-truck-loader__scene">
        {/* Wind streaks */}
        <span className="shipping-truck-loader__wind shipping-truck-loader__wind--one" />
        <span className="shipping-truck-loader__wind shipping-truck-loader__wind--two" />
        <span className="shipping-truck-loader__wind shipping-truck-loader__wind--three" />

        {/* Road */}
        <div className="shipping-truck-loader__road">
          <div className="shipping-truck-loader__road-line" />
        </div>

        {/* Truck */}
        <div className="shipping-truck-loader__truck">
          <LocalShippingIcon className="shipping-truck-loader__truck-icon" />
          <div className="shipping-truck-loader__wheel shipping-truck-loader__wheel--front" />
          <div className="shipping-truck-loader__wheel shipping-truck-loader__wheel--back" />
        </div>
      </Box>

      <Typography variant="body2" className="shipping-truck-loader__text">
        Loading shipping details…
      </Typography>
    </Box>
  );
};

// ─────────────────────────────
// Plane Loader (inline component)
// ─────────────────────────────
const ShippingPlaneLoader = () => {
  return (
    <Box className="shipping-plane-loader">
      <Box className="shipping-plane-loader__scene">
        <div className="shipping-plane-loader__sheen" />

        {/* Clouds */}
        <span className="shipping-plane-loader__cloud shipping-plane-loader__cloud--one" />
        <span className="shipping-plane-loader__cloud shipping-plane-loader__cloud--two" />
        <span className="shipping-plane-loader__cloud shipping-plane-loader__cloud--three" />

        {/* Ground / runway */}
        <div className="shipping-plane-loader__ground">
          <div className="shipping-plane-loader__runway-lights" />
          <div className="shipping-plane-loader__runway-line" />
        </div>

        {/* Plane */}
        <div className="shipping-plane-loader__planeWrap">
          <div className="shipping-plane-loader__contrail" />

          <div className="shipping-plane-loader__planeMark">
            {/* Package */}
            <div className="shipping-plane-loader__package" />
            <div className="shipping-plane-loader__packageLine" />

            <FlightTakeoffIcon className="shipping-plane-loader__planeIconOutline" />
            <FlightTakeoffIcon className="shipping-plane-loader__planeIcon" />
          </div>
        </div>
      </Box>

      <Typography variant="body2" className="shipping-plane-loader__text">
        Loading shipping details…
      </Typography>
    </Box>
  );
};

// ─────────────────────────────
// Boat Loader (inline component)
// ─────────────────────────────
const ShippingBoatLoader = () => {
  return (
    <Box className="shipping-boat-loader">
      <Box className="shipping-boat-loader__scene">
        <div className="shipping-boat-loader__sheen" />

        {/* Far clouds */}
        <span className="shipping-boat-loader__cloud shipping-boat-loader__cloud--one" />
        <span className="shipping-boat-loader__cloud shipping-boat-loader__cloud--two" />

        {/* Ocean */}
        <div className="shipping-boat-loader__ocean">
          <div className="shipping-boat-loader__wave shipping-boat-loader__wave--one" />
          <div className="shipping-boat-loader__wave shipping-boat-loader__wave--two" />
          <div className="shipping-boat-loader__wave shipping-boat-loader__wave--three" />
        </div>

        {/* Ship */}
        <div className="shipping-boat-loader__shipWrap">
          {/* Wake */}
          <div className="shipping-boat-loader__wake" />

          {/* Stacked containers */}
          <div className="shipping-boat-loader__containers">
            <span className="shipping-boat-loader__container c1" />
            <span className="shipping-boat-loader__container c2" />
            <span className="shipping-boat-loader__container c3" />
            <span className="shipping-boat-loader__container c4" />
            <span className="shipping-boat-loader__container c5" />
            <span className="shipping-boat-loader__container c6" />
          </div>

          <div className="shipping-boat-loader__buoy" />

          {/* Boat icon */}
          <DirectionsBoatIcon className="shipping-boat-loader__shipIconOutline" />
          <DirectionsBoatIcon className="shipping-boat-loader__shipIcon" />
        </div>
      </Box>

      <Typography variant="body2" className="shipping-boat-loader__text">
        Loading shipping details…
      </Typography>
    </Box>
  );
};

// ─────────────────────────────
// 33/33/33 Loader Wrapper (stable per mount)
// ─────────────────────────────
const RandomShippingLoader = () => {
  const [variant] = useState(() => {
    const r = Math.random();
    if (r < 0.333) return 'truck';
    if (r < 0.666) return 'plane';
    return 'boat';
  });

  if (variant === 'truck') return <ShippingTruckLoader />;
  if (variant === 'plane') return <ShippingPlaneLoader />;
  return <ShippingBoatLoader />;
};

// ─────────────────────────────
// ShippingDetails
// ─────────────────────────────
const ShippingDetails = ({ orderDetails: initialOrderData }) => {
  const { instanceUniqueId, orderId, tabId } = useParams();
  const history = useHistory();

  const [orderData, setOrderData] = useState(initialOrderData || null);
  const [shippingData, setShipping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [requireShippingConfirmation, setRequireShippingConfirmation] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [shipResp, orderResp] = await Promise.all([
        fetchShippingList(orderId),
        initialOrderData
          ? Promise.resolve({ data: initialOrderData })
          : getSingleOrderItem(orderId),
      ]);

      setShipping(shipResp.data);
      setOrderData(orderResp.data);
      setSelectedItems([]);
    } catch (err) {
      console.error('Error loading data', err);
    } finally {
      setLoading(false);
    }
  }, [orderId, initialOrderData]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    fetchOperationsManagementSettings(instanceUniqueId)
      .then((response) => {
        setRequireShippingConfirmation(
          response?.settings?.purchasing?.requireShippingConfirmation === true
        );
      })
      .catch(() => setRequireShippingConfirmation(false));
  }, [instanceUniqueId]);

  const getItemKey = useCallback((item, idx) => `${item.id}-${idx}`, []);
  const isItemSelected = (key) => selectedItems.includes(key);

  const bulkSelection = useMemo(
    () => deriveBulkShippingSelection(shippingData, selectedItems, getItemKey),
    [shippingData, selectedItems, getItemKey]
  );

  const handleRowSelect = (key, item) => {
    const supplierKey = getShippingSupplierKey(item);
    if (!bulkSelection.eligibleSupplierKeys.has(supplierKey)) return;
    if (bulkSelection.activeSupplierKey && bulkSelection.activeSupplierKey !== supplierKey) return;
    setSelectedItems((prev) =>
      prev.includes(key) ? prev.filter((selectedKey) => selectedKey !== key) : [...prev, key]
    );
  };

  const handleEditClick = (id) => {
    history.push(
      `/instance/${instanceUniqueId}/sales/orders/${orderId}/shipping/${id}`
    );
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    history.push(`/instance/${instanceUniqueId}/sales/orders/${orderId}/shipping/`);
  };

  const refreshShipping = async () => {
    await loadAll();
  };

  const ready = !loading && orderData && shippingData;

  return (
    <>
      {loading && (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <RandomShippingLoader />
        </Box>
      )}

      {ready && Array.isArray(shippingData) && (
        <ShippingTable
          shippingData={shippingData}
          getItemKey={getItemKey}
          isItemSelected={isItemSelected}
          handleRowSelect={handleRowSelect}
          handleEditClick={handleEditClick}
          orderId={orderData.OrderId}
          orderStatus={orderData.OrderStatus?.SO || ''}
          onGenerateShipping={loadAll}
          onRefresh={loadAll}
          onSaved={refreshShipping}
          bulkSelection={bulkSelection}
          onBulkEdit={() => setBulkOpen(true)}
          requireShippingConfirmation={requireShippingConfirmation}
        />
      )}

      <ShippingModal
        open={open}
        onClose={handleClose}
        setOpen={setOpen}
        product={
          Array.isArray(shippingData)
            ? shippingData.find((i) => `${i.id}` === tabId)
            : null
        }
        selectedShippingDetailsId={tabId}
        order={orderData}
        onSaved={refreshShipping}
      />

      <BulkShippingModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        selectedRows={bulkSelection.selectedRows}
        order={orderData}
        onComplete={async () => {
          setBulkOpen(false);
          setSelectedItems([]);
          await refreshShipping();
        }}
      />
    </>
  );
};

export default ShippingDetails;
